"use client";

import { Api } from "@/Apis";
import { CenterSiteTemplatePageImportGetListItemOutputApiModel } from "@/Apis/ClinicApi";
import { CenterCenterSelect } from "@/Areas/Center/Components/Selects";
import { CenterOptionTag } from "@/Areas/Center/Components/Tags";
import { ActionButton, CommonSelect } from "@/Components";
import { addColumnRowNumber, addRowNumber, formProps } from "@/Helpers/Constants";
import { useAccount, useT } from "@/Hooks";
import { TypedColumns } from "@/types/TypedColumns";
import { Button, Col, Flex, Form, Row, Space, Table } from "antd";
import { FC, useEffect, useState } from "react";

export interface CenterSiteTemplatePageImportPageProps {
  centerId?: number;
  callback: (result: boolean) => void;
}

interface CenterSiteTemplatePageImportPageForm {
  CenterId?: number;
  CenterSiteTemplateId?: number;
}

const CenterSiteTemplatePageImportPage: FC<
  CenterSiteTemplatePageImportPageProps
> = ({ centerId, callback }) => {
  const { profile } = useAccount();
  const t = useT();
  const [form] = Form.useForm<CenterSiteTemplatePageImportPageForm>();
  const centerSiteTemplateId = Form.useWatch("CenterSiteTemplateId", form);
  const selectedCenterId = Form.useWatch("CenterId", form);
  const [items, setItems] = useState<
    CenterSiteTemplatePageImportGetListItemOutputApiModel[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [hasImported, setHasImported] = useState(false);

  const getList = async (id?: number) => {
    if (!id) {
      setItems([]);
      return;
    }

    setLoading(true);
    try {
      const { Result } = await Api.CenterSiteTemplatePage.getImportList({
        CenterSiteTemplateId: id,
      });
      setItems(Result ?? []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void getList(centerSiteTemplateId);
  }, [centerSiteTemplateId]);

  const columns: TypedColumns<CenterSiteTemplatePageImportGetListItemOutputApiModel> = [
    {
      title: t("common.field.pageType", undefined, "Page Type"),
      dataIndex: "CenterSiteTemplatePageType",
      render: (value) => <CenterOptionTag model={value} />,
    },
    {
      title: t("common.field.path", undefined, "Path"),
      dataIndex: "Path",
    },
    {
      title: t("common.field.title", undefined, "Title"),
      dataIndex: "Title",
    },
    {
      title: t("common.field.status", undefined, "Status"),
      dataIndex: "Status",
      render: (value) => <CenterOptionTag model={value} />,
    },
    {
      title: t("common.field.operations", undefined, "Operations"),
      render: (_, record) => (
        <ActionButton
          type="primary"
          confirm
          onClick={async () => {
            if (!centerSiteTemplateId) return;
            const result = await Api.CenterSiteTemplatePage.import({
              CenterSiteTemplateId: centerSiteTemplateId,
              SourceCenterSiteTemplatePageId: record.Id,
            });
            if (!result.Success) return;

            setHasImported(true);
            await getList(centerSiteTemplateId);
          }}
        >
          {t("common.action.import", undefined, "Import")}
        </ActionButton>
      ),
    },
  ];

  return (
    <Space orientation="vertical" className="w-100">
      <Form
        {...formProps}
        form={form}
        initialValues={{
          CenterId: centerId ?? profile?.Center?.Id,
          CenterSiteTemplateId:
            (centerId ?? profile?.Center?.Id) === profile?.Center?.Id
              ? profile?.DomainActiveCenterSiteTemplateId
              : undefined,
        }}
      >
        <Row>
          <Col md={12} xs={24}>
            <Form.Item
              name="CenterId"
              label={t("common.field.center", undefined, "Center")}
              rules={[{ required: true }]}
            >
              <CenterCenterSelect
                CurrentCenterAndDescendantsOnly
                onChange={() => form.setFieldValue("CenterSiteTemplateId", undefined)}
              />
            </Form.Item>
          </Col>
          <Col md={12} xs={24}>
            <Form.Item
              name="CenterSiteTemplateId"
              label={t("common.field.siteTemplate", undefined, "Site Template")}
              rules={[{ required: true }]}
            >
              <CommonSelect
                sourceType="Center"
                methodName="SiteTemplates"
                model={{ CenterId: selectedCenterId }}
                allowClear
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Table<CenterSiteTemplatePageImportGetListItemOutputApiModel>
        columns={addColumnRowNumber(columns)}
        dataSource={addRowNumber(items)}
        rowKey={(record) => record.Id}
        loading={loading}
        pagination={false}
      />
      <Flex justify="end" gap={8}>
        <Button onClick={() => callback(hasImported)}>
          {t("common.action.cancel", undefined, "Cancel")}
        </Button>
      </Flex>
    </Space>
  );
};

export default CenterSiteTemplatePageImportPage;
